// Entry-point steering for a paired Prism install (Plex app.plex.tv shape:
// connect the user, don't make them pick a network). Browsers can't set
// SNI, and an https page can't probe an http LAN URL (mixed content) — only
// same-scheme (https public) probing and cross-scheme top-level navigation
// are reliable. See decision-web-access-three-networks-2026-08-23 in
// PreteServer-dev — the constraints here are law, not re-derived.

import { PUBLIC_ENTRY_HOST } from "../config";

export interface ServerHeartbeat {
  last_lan_ip: string | null;
  last_lan_port: number | null;
  last_seen_at: string | null;
}

export interface ServerEntryLinks {
  homeUrl: string | null;
  awayUrl: string | null;
}

// No distinct public host/port column exists on user_servers today — the
// operator's gateway forwards the SAME port for LAN and WAN (see
// workers/auth/src/routes/servers.ts DEFAULT_LAN_PORT and the 7477/7478
// install notes in this repo's handoffs/), so the away link reuses
// last_lan_port against the fixed public hostname. Productization gap: an
// install forwarded on a public port that differs from its LAN port can't
// express that yet — this constant would need to become a per-row field.
export function serverEntryLinks(s: ServerHeartbeat): ServerEntryLinks {
  // Gate on a completed heartbeat, not just a pair-time IP: lan_ip can be
  // set at pair time before the streamer has ever phoned home, and a link
  // with no heartbeat evidence behind it should read as "never seen," not
  // as a plausibly-dead clickable URL.
  if (!s.last_lan_ip || !s.last_lan_port || !s.last_seen_at) {
    return { homeUrl: null, awayUrl: null };
  }
  return {
    homeUrl: `http://${s.last_lan_ip}:${s.last_lan_port}`,
    awayUrl: `https://${PUBLIC_ENTRY_HOST}:${s.last_lan_port}`,
  };
}

// Streamer heartbeat cadence is ~30s (handoffs/task-architect-prism-trust-
// ios-resolver.md). Several missed beats of grace before "stale" so one
// slow tick doesn't flip the badge.
const HEARTBEAT_INTERVAL_MS = 30_000;
const STALE_AFTER_MS = HEARTBEAT_INTERVAL_MS * 6;

export type ServerOnlineState = "online" | "stale" | "never_seen";

export function serverOnlineState(
  s: Pick<ServerHeartbeat, "last_seen_at">,
  now: number = Date.now(),
): ServerOnlineState {
  if (!s.last_seen_at) return "never_seen";
  const seenAt = Date.parse(s.last_seen_at);
  if (Number.isNaN(seenAt)) return "never_seen";
  return now - seenAt <= STALE_AFTER_MS ? "online" : "stale";
}

// ---- Auto-connect decision tree ----

export type EntryChoice = "home" | "away";

export interface AutoEntryPlan {
  // "offline"  — don't navigate; render the offline/stale state.
  // "navigate" — go straight to `url` (remembered LAN fast path, or an
  //              already-resolved probe outcome).
  // "probe"    — caller must probe the public entry, then call
  //              resolveAfterProbe() with the result.
  action: "offline" | "navigate" | "probe";
  choice?: EntryChoice;
  url?: string;
}

// Fast-path decision, before any network probe runs.
export function planAutoEntry(
  links: ServerEntryLinks,
  online: ServerOnlineState,
  remembered: EntryChoice | null,
): AutoEntryPlan {
  if (online !== "online" || !links.homeUrl || !links.awayUrl) {
    // never_seen (no links yet) or stale (heartbeat old enough the server
    // may genuinely be down) both surface the offline state instead of
    // guessing which network the user is on.
    return { action: "offline" };
  }
  if (remembered === "home") {
    // Instant reconnect for the common "I'm home again" case — no probe
    // round-trip, no spinner. Improvement over Plex's always-probe flow.
    return { action: "navigate", choice: "home", url: links.homeUrl };
  }
  return { action: "probe" };
}

// Resolve the plan once the caller has probed the public entry.
export function resolveAfterProbe(links: ServerEntryLinks, publicReachable: boolean): AutoEntryPlan {
  if (publicReachable && links.awayUrl) {
    return { action: "navigate", choice: "away", url: links.awayUrl };
  }
  if (links.homeUrl) {
    return { action: "navigate", choice: "home", url: links.homeUrl };
  }
  return { action: "offline" };
}

// ---- Remembered entry choice (per device, per server) ----

const REMEMBERED_KEY_PREFIX = "prism-entry-choice:";

export function readRememberedEntry(serverId: string): EntryChoice | null {
  try {
    const v = localStorage.getItem(REMEMBERED_KEY_PREFIX + serverId);
    return v === "home" || v === "away" ? v : null;
  } catch {
    return null;
  }
}

export function writeRememberedEntry(serverId: string, choice: EntryChoice): void {
  try {
    localStorage.setItem(REMEMBERED_KEY_PREFIX + serverId, choice);
  } catch {
    /* best-effort convenience only */
  }
}
